import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';

const primaryVariantStyle = css`
  background: ${({ theme }) => theme.colors.mainColor};
  color: ${({ theme }) => theme.colors.backgroundColorWhite};

  ${({ theme }) => theme.shadows.buttonEnableShadow};

  svg path{
    fill: ${({ theme }) => theme.colors.backgroundColorWhite};
  }

  :hover {
    ${({ theme }) => theme.shadows.buttonHoverShadow};
  }

  :focus {
    box-shadow: none;
    padding: 12px 30px;
    border: 2px solid ${({ theme }) => theme.colors.mainColorDarker};
  }

  :active {
    border: none;
    padding: 12px 32px;
    background-color: ${({ theme }) => theme.colors.mainColorDarker};
    ${({ theme }) => theme.shadows.buttonPressedShadow};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.gray400};
    color: ${({ theme }) => theme.colors.gray300};
    ${({ theme }) => theme.shadows.buttonDisabledShadow};

    svg path{
      fill: ${({ theme }) => theme.colors.gray300};
    }
  }
`;

const secondaryVariantStyle = css`
  border: 2px solid ${({ theme }) => theme.colors.mainColor};
  background: ${({ theme }) => theme.colors.backgroundColorWhite};
  color: ${({ theme }) => theme.colors.mainColor};

  svg path{
    fill: ${({ theme }) => theme.colors.mainColor};
  }

  :hover {
    background: ${({ theme }) => theme.colors.mainColorBackground};
    ${({ theme }) => theme.shadows.buttonHoverShadow};
  }

  :focus {
    background: ${({ theme }) => theme.colors.mainColorBackground};
    box-shadow: none;
  }

  :active {
    border: none;
    color: ${({ theme }) => theme.colors.mainColorDarker};
    border-color: ${({ theme }) => theme.colors.mainColorDarker};

    svg path{
      fill: ${({ theme }) => theme.colors.mainColorDarker};
    }
  }

  :disabled {
    background-color: ${({ theme }) => theme.colors.backgroundColorLight};
    border-color: ${({ theme }) => theme.colors.gray400};
    color: ${({ theme }) => theme.colors.gray400};

    svg path{
      fill: ${({ theme }) => theme.colors.gray400};
    }

    :hover {
      box-shadow: none;
    }
  }
`;

const linkVariantStyle = css`
  color: ${({ theme }) => theme.colors.mainColor};
  background: transparent;

  svg path{
    fill: ${({ theme }) => theme.colors.mainColor};
  }

  :hover {
    background: ${({ theme }) => theme.colors.mainColorBackground};
  }

  :focus {
    box-shadow: none;
    border: 2px solid ${({ theme }) => theme.colors.mainColorLighter};
  }

  :active {
    border: none;
    color: ${({ theme }) => theme.colors.mainColorDarker};

    svg path{
      fill: ${({ theme }) => theme.colors.mainColorDarker};
    }
  }

  :disabled {
    color: ${({ theme }) => theme.colors.gray400};

    svg path{
      fill: ${({ theme }) => theme.colors.gray400};
    }

    :hover {
      background-color: transparent;
    }
  }
`;

const getVariantStyle = ({ variant }) => {
  if (variant === 'secondary') {
    return secondaryVariantStyle;
  }

  if (variant === 'link') {
    return linkVariantStyle;
  }

  return primaryVariantStyle;
};

const getLoadingStyle = ({ theme }) => css`
  transition: 0.1s;
  background: ${theme.colors.mainColorDarker};
  color: ${theme.colors.backgroundColorWhite};
  ${theme.shadows.buttonPressedShadow};
`;

const StyledButton = styled.button`
  ${({ theme }) => theme.typograph.ctaLabel}

  height: 44px; 
  width: fit-content;
  padding: 12px 32px;
  display: flex;
  align-items: center;

  border-radius: 2px;
  border: none;
  cursor: pointer;

  transition: 0.1s;

  div {
    height: 20px;
    margin-right: 8px;
  }

  span {
    color: inherit;
  }

  ${({ isLoading }) => (isLoading ? getLoadingStyle : getVariantStyle)}

  :disabled {
    cursor: not-allowed;
  }
`;

const LoaderWrapper = styled(motion.span)`
  display: flex;
  align-items: center;
  margin-right: 0px;
  margin-left: 12px;

  span {
    margin-bottom: 0px;
    margin-top: 0px;
  }
`;

export {
  StyledButton,
  LoaderWrapper,
};
